import { useEffect, useState } from 'react';
import { Trans } from '../i18n/Trans';
import { ApiHelper } from '../helper/ApiHelper';

interface KicadInfo {
  localBackend: string;
  localFrontend: string;
  minKicad: string;
  pluginDir: string;
  downloadUrl: string;
  packageVersion: string;
  featureKeys: string[];
  stepKeys: string[];
  needKeys: string[];
}

/**
 * Tab 2 KiCad 线路板开发插件。
 * 插件本体装在用户自己的 KiCad 里（桌面侧栏 + 本机后端），网页这边负责：把安装包发给他、
 * 讲清怎么装、提供本机面板入口。参数由后端 /api/kicad/info 下发，改端口不用重新发版。
 *
 * 注意：不在这里探活本机 127.0.0.1 —— 站点是 https，浏览器会按混合内容拦掉 http 子请求；
 * 顶层跳转不受此限，所以入口做成"打开"按钮而不是状态灯。
 */
export function KicadPluginPage() {
  const [info, setInfo] = useState<KicadInfo | null>(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    ApiHelper.call<{ info: KicadInfo }>('/api/kicad/info')
      .then((r) => setInfo(r.info))
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, []);

  if (err) return <div className="error-box">{err}</div>;
  if (!info) return <div className="hint">{Trans.t('common.loading')}</div>;

  // 安装包没打好就整条「下载」都不出现（连步骤里那一步也不出现）：
  // 写着"下载安装包"却没有能点的东西，比不写这一步更糟——用户会一直找。
  const hasPkg = !!info.downloadUrl;

  return (
    <div>
      {/* 同估价页：不写标题也不写导语。这一页的"介绍"就是下面三张卡片
          （能干什么 / 装之前备齐什么 / 怎么装），它们本身自解释，
          再在顶上压一段功能说明只是把主操作往下推 */}
      <div className="cards">
        <div className="card">
          <div className="card-title">{Trans.t('kicad.canDo')}</div>
          <ul>{info.featureKeys.map((k) => <li key={k}>{Trans.t(k)}</li>)}</ul>
        </div>
        <div className="card">
          {/* 前置条件放在下载按钮**之前**：装到一半才发现少个 Node 最耽误人 */}
          <div className="card-title">{Trans.t('kicad.need')}</div>
          <ul>{info.needKeys.map((k) => <li key={k}>{Trans.t(k)}</li>)}</ul>
        </div>
        <div className="card">
          <div className="card-title">{Trans.t('kicad.install')}</div>
          <ol>
            {hasPkg && <li>{Trans.t('kicad.step.download')}</li>}
            {info.stepKeys.map((k) => <li key={k}>{Trans.t(k)}</li>)}
          </ol>
          <div className="kv"><span>{Trans.t('kicad.pluginDir')}</span><code>{info.pluginDir}</code></div>
          <div className="kv"><span>{Trans.t('kicad.minVer')}</span><code>KiCad {info.minKicad}+</code></div>
        </div>
      </div>

      <div className="field">
        {/* 主操作是"下载"——没装过的人才是这一页的主要读者；打开本机面板是装完之后的事 */}
        {hasPkg
          ? <a className="btn accent" href={info.downloadUrl}>{Trans.t('kicad.download')}</a>
          : <span className="hint">{Trans.t('kicad.noPackage')}</span>}
        <button className="btn ghost" onClick={() => window.open(info.localBackend, '_blank', 'noopener')}>
          {Trans.t('kicad.openLocal')}
        </button>
        {hasPkg && <span className="hint">v{info.packageVersion} · {Trans.t('kicad.downloadHint')}</span>}
      </div>
      <div className="hint">{Trans.t('kicad.openHint')} <code>{info.localBackend}</code></div>
    </div>
  );
}
